import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import SearchSelect from "../../../ui/groups/SearchSelect";
import { useEscapeKey } from "../../../../hooks/useEscapeKey";
import { ICatalogItem, fetchEditorCatalogs, savePrefilter } from "./prefiltradoService";
import { IPrefilter, IPrefilterDraft, PrefilterGender, draftFromPrefilter } from "./prefiltradoTypes";

const FONT = "Roboto, sans-serif";

interface EditorPrefiltroProps {
  /** null = nuevo. */
  prefilter: IPrefilter | null;
  /** Duplicar: parte de `prefilter` pero guarda uno nuevo. */
  asCopy: boolean;
  /** Clientes que ya tienen prefiltro (pueden no estar en el catálogo de asignables). */
  knownClients: ICatalogItem[];
  onClose: () => void;
  onSaved: () => void;
}

const GENDER_OPTIONS: { value: PrefilterGender; label: string; help: string }[] = [
  { value: "TODOS", label: "Mixto", help: "No filtra por género." },
  { value: "MASCULINO", label: "Solo masculino", help: "Excluye a quien no sea masculino." },
  { value: "FEMENINO", label: "Solo femenino", help: "Excluye a quien no sea femenino." },
  { value: "CUOTA", label: "Cuota", help: "No excluye: muestra la proporción contra la meta." },
];

const EditorPrefiltro = ({ prefilter, asCopy, knownClients, onClose, onSaved }: EditorPrefiltroProps) => {
  const [draft, setDraft] = useState<IPrefilterDraft>(() => draftFromPrefilter(prefilter, asCopy));
  const [catalogs, setCatalogs] = useState<Awaited<ReturnType<typeof fetchEditorCatalogs>> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localitySearch, setLocalitySearch] = useState("");
  const [region, setRegion] = useState("");

  useEscapeKey(onClose, !saving);

  useEffect(() => {
    let cancelled = false;
    fetchEditorCatalogs().then((data) => { if (!cancelled) setCatalogs(data); });
    return () => { cancelled = true; };
  }, []);

  const set = <K extends keyof IPrefilterDraft>(key: K, value: IPrefilterDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const toggle = (key: "positions" | "shifts", value: string) =>
    setDraft((current) => ({
      ...current,
      [key]: current[key].includes(value) ? current[key].filter((v) => v !== value) : [...current[key], value],
    }));

  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    [...knownClients, ...(catalogs?.clients ?? [])].forEach((c) => { if (!byId.has(c.value)) byId.set(c.value, c.label); });
    return Array.from(byId, ([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [catalogs, knownClients]);

  const localities = useMemo(() => catalogs?.localities ?? [], [catalogs]);
  // Al editar, las comunas guardadas se nombran aunque el catálogo aún no cargue.
  const localityNameById = useMemo(() => {
    const names = new Map<number, string>((prefilter?.localities ?? []).map((l) => [l.localityId, l.name]));
    localities.forEach((l) => names.set(Number(l.value), l.label));
    return names;
  }, [localities, prefilter]);

  const regions = useMemo(
    () => Array.from(new Set(localities.map((l) => l.group).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b, "es")),
    [localities],
  );

  const filteredLocalities = useMemo(() => {
    const term = localitySearch.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return localities.filter((l) => {
      if (region && l.group !== region) return false;
      if (!term) return true;
      return l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(term);
    });
  }, [localities, localitySearch, region]);

  const toggleLocality = (id: number) =>
    setDraft((current) => ({
      ...current,
      localityIds: current.localityIds.includes(id)
        ? current.localityIds.filter((v) => v !== id)
        : [...current.localityIds, id],
    }));

  const addVisibleLocalities = () =>
    setDraft((current) => ({
      ...current,
      localityIds: Array.from(new Set([...current.localityIds, ...filteredLocalities.map((l) => Number(l.value))])),
    }));

  // Opciones de cargo y turno: catálogo + lo que ya tenga guardado el prefiltro.
  const positionOptions = useMemo(
    () => Array.from(new Set([...(catalogs?.positions ?? []).map((p) => p.value), ...draft.positions])).sort(),
    [catalogs, draft.positions],
  );
  const shiftOptions = useMemo(
    () => Array.from(new Set([...(catalogs?.shifts ?? []).map((s) => s.value), ...draft.shifts])).sort(),
    [catalogs, draft.shifts],
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await savePrefilter(draft);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar el prefiltro.");
    } finally {
      setSaving(false);
    }
  };

  const title = !prefilter ? "Nuevo prefiltro" : asCopy ? `Duplicar «${prefilter.name}»` : `Editar «${prefilter.name}»`;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1300, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: "min(860px, 100%)", background: "#fff", borderRadius: 14, fontFamily: FONT, color: "#1e293b",
          boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{title}</h3>
          <button type="button" onClick={onClose} disabled={saving} style={iconButton} aria-label="Cerrar">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </header>

        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 18 }}>
          <Fila>
            <Campo label="Cliente" flex={2}>
              <SearchSelect
                value={draft.clientId}
                options={clientOptions}
                onChange={(code) => set("clientId", code)}
                placeholder={catalogs ? "Selecciona un cliente" : "Cargando clientes…"}
                clearable={false}
                ariaLabel="Cliente"
              />
            </Campo>
            <Campo label="Sede / división" flex={2}>
              <input value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="Loft 1, Pudahuel, La Farfana…" style={input} />
            </Campo>
            <Campo label="Campaña" flex={1}>
              <input value={draft.campaign} onChange={(e) => set("campaign", e.target.value)} placeholder="Cyber 2026" style={input} />
            </Campo>
          </Fila>

          <Fila>
            <Campo label="Edad mínima" flex={1}>
              <input type="number" min={14} max={99} value={draft.ageMin} onChange={(e) => set("ageMin", e.target.value)} style={input} />
            </Campo>
            <Campo label="Edad máxima" flex={1}>
              <input type="number" min={14} max={99} value={draft.ageMax} onChange={(e) => set("ageMax", e.target.value)} style={input} />
            </Campo>
            <Campo label="Postulaciones desde (opcional)" flex={1.4}>
              <input type="date" value={draft.applicationsFrom} onChange={(e) => set("applicationsFrom", e.target.value)} style={input} />
            </Campo>
            <Campo label="Orden" flex={0.6}>
              <input type="number" value={draft.order} onChange={(e) => set("order", e.target.value)} style={input} />
            </Campo>
          </Fila>

          <Campo label="Género">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {GENDER_OPTIONS.map((option) => (
                <label key={option.value} title={option.help} style={pill(draft.gender === option.value)}>
                  <input
                    type="radio"
                    name="gender"
                    checked={draft.gender === option.value}
                    onChange={() => set("gender", option.value)}
                    style={{ display: "none" }}
                  />
                  {option.label}
                </label>
              ))}
              {draft.gender === "CUOTA" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input
                    type="number" min={0} max={100} value={draft.maleQuota}
                    onChange={(e) => set("maleQuota", e.target.value)}
                    style={{ ...input, width: 70 }} aria-label="Porcentaje de hombres"
                  />
                  % hombres
                </span>
              )}
            </div>
          </Campo>

          <Campo label={`Comunas (${draft.localityIds.length} seleccionadas)`}>
            {draft.localityIds.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8, maxHeight: 92, overflowY: "auto" }}>
                {draft.localityIds
                  .map((id) => ({ id, name: localityNameById.get(id) ?? `#${id}` }))
                  .sort((a, b) => a.name.localeCompare(b.name, "es"))
                  .map(({ id, name }) => (
                    <button key={id} type="button" onClick={() => toggleLocality(id)} style={tag} title="Quitar">
                      {name} ×
                    </button>
                  ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <input
                value={localitySearch}
                onChange={(e) => setLocalitySearch(e.target.value)}
                placeholder="Buscar comuna"
                style={{ ...input, flex: 1, minWidth: 160 }}
              />
              <select value={region} onChange={(e) => setRegion(e.target.value)} style={{ ...input, width: 240 }} aria-label="Región">
                <option value="">Todas las regiones</option>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="button" onClick={addVisibleLocalities} disabled={filteredLocalities.length === 0} style={secondaryButton}>
                Agregar las {filteredLocalities.length} visibles
              </button>
              {draft.localityIds.length > 0 && (
                <button type="button" onClick={() => set("localityIds", [])} style={secondaryButton}>Quitar todas</button>
              )}
            </div>
            <div style={{
              border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 170, overflowY: "auto",
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", padding: 6,
            }}>
              {!catalogs ? (
                <span style={{ fontSize: 12.5, color: "#94a3b8", padding: 6 }}>Cargando comunas…</span>
              ) : filteredLocalities.map((l) => (
                <label key={l.value} style={checkRow}>
                  <input
                    type="checkbox"
                    checked={draft.localityIds.includes(Number(l.value))}
                    onChange={() => toggleLocality(Number(l.value))}
                  />
                  {l.label}
                </label>
              ))}
            </div>
          </Campo>

          <Campo label="Cargos aceptados (excluyen; quien no eligió cargo se muestra aparte)">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {positionOptions.map((p) => (
                <label key={p} style={pill(draft.positions.includes(p))}>
                  <input type="checkbox" checked={draft.positions.includes(p)} onChange={() => toggle("positions", p)} style={{ display: "none" }} />
                  {p}
                </label>
              ))}
            </div>
          </Campo>

          <Campo label="Turnos del formulario">
            <label style={{ ...checkRow, marginBottom: 6 }}>
              <input type="checkbox" checked={draft.shiftExclusive} onChange={(e) => set("shiftExclusive", e.target.checked)} />
              El turno excluye (solo si el formulario del cliente ofrece estos mismos turnos)
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, opacity: draft.shiftExclusive ? 1 : 0.6 }}>
              {shiftOptions.map((s) => (
                <label key={s} style={pill(draft.shifts.includes(s))}>
                  <input type="checkbox" checked={draft.shifts.includes(s)} onChange={() => toggle("shifts", s)} style={{ display: "none" }} />
                  {s}
                </label>
              ))}
            </div>
          </Campo>

          <Fila>
            <Campo label="Extranjeros" flex={1}>
              <label style={checkRow}>
                <input type="checkbox" checked={draft.requireVisa} onChange={(e) => set("requireVisa", e.target.checked)} />
                Exigir visa registrada (temporal o definitiva)
              </label>
            </Campo>
            <Campo label="Jornada (informativo)" flex={1}>
              <input value={draft.workday} onChange={(e) => set("workday", e.target.value)} placeholder="5x2 Rotativo" style={input} />
            </Campo>
          </Fila>

          <Fila>
            <Campo label="Horarios (informativo)" flex={1}>
              <textarea value={draft.schedule} onChange={(e) => set("schedule", e.target.value)} rows={4} style={{ ...input, resize: "vertical" }} />
            </Campo>
            <Campo label="Observaciones" flex={1}>
              <textarea value={draft.notes} onChange={(e) => set("notes", e.target.value)} rows={4} style={{ ...input, resize: "vertical" }} />
            </Campo>
          </Fila>

          <label style={checkRow}>
            <input type="checkbox" checked={draft.active} onChange={(e) => set("active", e.target.checked)} />
            Vigente (si se desmarca, deja de evaluarse y solo lo ven los administradores)
          </label>

          {error && (
            <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(239,68,68,0.10)", color: "#b91c1c", fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>

        <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 20px", borderTop: "1px solid #f1f5f9" }}>
          <button type="button" onClick={onClose} disabled={saving} style={secondaryButton}>Cancelar</button>
          <button type="button" onClick={handleSave} disabled={saving} style={primaryButton}>
            {saving && <CircularProgress size={14} style={{ color: "#fff" }} />}
            Guardar
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

const Fila = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>{children}</div>
);

const Campo = ({ label, flex, children }: { label: string; flex?: number; children: React.ReactNode }) => (
  <div style={{ flex: flex ?? "1 1 100%", minWidth: 140, display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{label}</span>
    {children}
  </div>
);

const input: React.CSSProperties = {
  width: "100%", padding: "7px 10px", fontSize: 13, fontFamily: FONT, border: "1px solid #cbd5e1",
  borderRadius: 8, outline: "none", color: "#1e293b", background: "#fff", boxSizing: "border-box",
};

const checkRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#334155", padding: "3px 4px", cursor: "pointer",
};

const pill = (active: boolean): React.CSSProperties => ({
  padding: "5px 12px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", userSelect: "none",
  border: `1px solid ${active ? "#0065DD" : "#cbd5e1"}`,
  background: active ? "rgba(0,101,221,0.08)" : "#fff",
  color: active ? "#0065DD" : "#334155",
  fontWeight: active ? 600 : 400,
});

const tag: React.CSSProperties = {
  border: "none", borderRadius: 999, padding: "3px 10px", fontSize: 11.5, cursor: "pointer",
  background: "#eff6ff", color: "#1d4ed8", fontFamily: FONT,
};

const primaryButton: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none",
  background: "#0065DD", color: "#fff", fontFamily: FONT, fontSize: 13, fontWeight: 500, cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  ...primaryButton, background: "#fff", color: "#334155", border: "1px solid #e2e8f0",
};

const iconButton: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32,
  borderRadius: 8, border: "none", background: "transparent", color: "#64748b", cursor: "pointer",
};

export default EditorPrefiltro;
